/**
 * DDReportPanel - Due Diligence Report Generation
 *
 * Generates real DD reports by calling the Azure backend API.
 * Persists active job state to localStorage so progress survives tab switches.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import type { CompanyData, ReportSection, Report, ReportQuality } from '../../types'
import { generateReport, getReportStatus, listReports, downloadReport, type ReportListItem } from '../../services/api'

const ACTIVE_JOB_KEY = 'pe-diligence-active-report-job'

interface ActiveJobState {
  jobId: string
  ticker: string
  reportType: string
  startedAt: number
  progress: number
}

interface DDReportPanelProps {
  company: CompanyData | null
  onViewReport?: (report: Report) => void
}

type ReportType = 'quick' | 'full' | 'custom'
type ReportStatus = 'idle' | 'generating' | 'completed' | 'failed'

// Sections for public companies (have SEC filings, financial data)
const publicSections: ReportSection[] = [
  { id: 'profile', name: 'Company Profile', status: 'pending', progress: 0 },
  { id: 'financial', name: 'Financial Analysis', status: 'pending', progress: 0 },
  { id: 'research', name: 'Multi-Source Research', status: 'pending', progress: 0 },
  { id: 'regime', name: 'Regime Classification', status: 'pending', progress: 0 },
  { id: 'exec_summary', name: 'Executive Summary', status: 'pending', progress: 0 },
  { id: 'qa', name: 'DD Questions', status: 'pending', progress: 0 },
  { id: 'risk', name: 'Risk Assessment', status: 'pending', progress: 0 },
  { id: 'data_room', name: 'Data Room Suggestions', status: 'pending', progress: 0 },
]

// Sections for private companies (focus on research, qualitative analysis)
const privateSections: ReportSection[] = [
  { id: 'disclaimer', name: 'Private Company Disclaimer', status: 'pending', progress: 0 },
  { id: 'profile', name: 'Company Profile & Background', status: 'pending', progress: 0 },
  { id: 'ownership', name: 'Ownership & Management Research', status: 'pending', progress: 0 },
  { id: 'industry', name: 'Industry & Market Analysis', status: 'pending', progress: 0 },
  { id: 'competition', name: 'Competitive Landscape', status: 'pending', progress: 0 },
  { id: 'news', name: 'News & Public Information', status: 'pending', progress: 0 },
  { id: 'exec_summary', name: 'Executive Summary', status: 'pending', progress: 0 },
  { id: 'qa', name: 'Due Diligence Questions', status: 'pending', progress: 0 },
  { id: 'risk', name: 'Preliminary Risk Assessment', status: 'pending', progress: 0 },
  { id: 'data_room', name: 'Data Room Requirements', status: 'pending', progress: 0 },
]

// Sections for ETFs (focus on holdings, tracking, costs)
const etfSections: ReportSection[] = [
  { id: 'overview', name: 'ETF Overview & Strategy', status: 'pending', progress: 0 },
  { id: 'holdings', name: 'Top Holdings Analysis', status: 'pending', progress: 0 },
  { id: 'sector', name: 'Sector & Geographic Allocation', status: 'pending', progress: 0 },
  { id: 'performance', name: 'Performance vs Benchmark', status: 'pending', progress: 0 },
  { id: 'tracking', name: 'Tracking Error Analysis', status: 'pending', progress: 0 },
  { id: 'costs', name: 'Expense Ratio & Costs', status: 'pending', progress: 0 },
  { id: 'liquidity', name: 'Liquidity & Trading Volume', status: 'pending', progress: 0 },
  { id: 'alternatives', name: 'Alternative ETFs Comparison', status: 'pending', progress: 0 },
  { id: 'risk', name: 'Risk Metrics', status: 'pending', progress: 0 },
]

// Sections for Mutual Funds (focus on manager, style, fees)
const mutualFundSections: ReportSection[] = [
  { id: 'overview', name: 'Fund Overview & Objective', status: 'pending', progress: 0 },
  { id: 'manager', name: 'Manager Track Record', status: 'pending', progress: 0 },
  { id: 'holdings', name: 'Portfolio Holdings', status: 'pending', progress: 0 },
  { id: 'style', name: 'Investment Style Analysis', status: 'pending', progress: 0 },
  { id: 'performance', name: 'Performance History', status: 'pending', progress: 0 },
  { id: 'fees', name: 'Fee Structure & Loads', status: 'pending', progress: 0 },
  { id: 'turnover', name: 'Portfolio Turnover', status: 'pending', progress: 0 },
  { id: 'risk', name: 'Risk Profile & Volatility', status: 'pending', progress: 0 },
  { id: 'alternatives', name: 'Alternative Funds', status: 'pending', progress: 0 },
]

// Default to public sections (will be overridden based on company type)
const defaultSections = publicSections

// Extended report type to include real content
interface ExtendedReport extends Report {
  content?: string
  report_id?: string
  download_url?: string
  quality?: ReportQuality
}

// Helper functions for persisting active job state
function saveActiveJob(job: ActiveJobState) {
  try {
    localStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify(job))
  } catch (e) {
    console.warn('Failed to save active job:', e)
  }
}

function loadActiveJob(): ActiveJobState | null {
  try {
    const saved = localStorage.getItem(ACTIVE_JOB_KEY)
    if (saved) {
      const job = JSON.parse(saved) as ActiveJobState
      // Check if job is not too old (max 30 minutes)
      if (Date.now() - job.startedAt < 30 * 60 * 1000) {
        return job
      }
      // Clear stale job
      localStorage.removeItem(ACTIVE_JOB_KEY)
    }
  } catch (e) {
    console.warn('Failed to load active job:', e)
  }
  return null
}

function clearActiveJob() {
  try {
    localStorage.removeItem(ACTIVE_JOB_KEY)
  } catch (e) {
    console.warn('Failed to clear active job:', e)
  }
}

// Helper to get sections based on company quote type
function getSectionsForCompanyType(company: CompanyData | null): ReportSection[] {
  if (!company) return publicSections

  const quoteType = company.quoteType
  if (company.isPrivate || quoteType === 'PRIVATE') return privateSections
  if (quoteType === 'ETF') return etfSections
  if (quoteType === 'MUTUALFUND') return mutualFundSections
  return publicSections
}

// Helper to get company type label
function getCompanyTypeLabel(company: CompanyData | null): string {
  if (!company) return 'EQUITY'
  const quoteType = company.quoteType
  if (company.isPrivate || quoteType === 'PRIVATE') return 'PRIVATE'
  if (quoteType === 'ETF') return 'ETF'
  if (quoteType === 'MUTUALFUND') return 'MUTUAL FUND'
  return 'EQUITY'
}

export function DDReportPanel({ company, onViewReport }: DDReportPanelProps) {
  // Detect company type
  const isPrivate = company?.isPrivate || company?.quoteType === 'PRIVATE'
  const isETF = company?.quoteType === 'ETF'
  const isMutualFund = company?.quoteType === 'MUTUALFUND'
  const companyTypeLabel = getCompanyTypeLabel(company)

  // Use appropriate sections based on company type
  const initialSections = getSectionsForCompanyType(company)

  const [reportType, setReportType] = useState<ReportType>('quick')
  const [status, setStatus] = useState<ReportStatus>('idle')
  const [sections, setSections] = useState<ReportSection[]>(initialSections)
  const [selectedSections, setSelectedSections] = useState<Set<string>>(new Set(initialSections.map(s => s.id)))
  const [overallProgress, setOverallProgress] = useState(0)
  const [viewingReport, setViewingReport] = useState<ExtendedReport | null>(null)
  const [generatedReport, setGeneratedReport] = useState<ExtendedReport | null>(null)
  const [recentReports, setRecentReports] = useState<ReportListItem[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string>('')

  // Ref to track active polling
  const pollingRef = useRef<number | undefined>(undefined)
  const jobIdRef = useRef<string | null>(null)
  const mountedRef = useRef(true)

  // Start polling for a given job ID
  const startPolling = useCallback((jobId: string, currentReportType: ReportType) => {
    // Clear any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = undefined
    }

    jobIdRef.current = jobId

    pollingRef.current = window.setInterval(async () => {
      try {
        const statusResult = await getReportStatus(jobId)

        if (!mountedRef.current) return

        if (statusResult.error) {
          console.error('Status poll error:', statusResult.error)
          return
        }

        const jobStatus = statusResult.data
        if (!jobStatus) return

        // Update progress and message
        setOverallProgress(jobStatus.progress)
        setStatusMessage(jobStatus.message || '')

        // Update persisted job state
        const activeJob = loadActiveJob()
        if (activeJob && activeJob.jobId === jobId) {
          saveActiveJob({ ...activeJob, progress: jobStatus.progress })
        }

        // Update sections based on progress
        // Use the actual sections length (varies by company type: ETF=9, MUTUALFUND=9, PRIVATE=10, PUBLIC=8)
        setSections(prev => {
          const sectionCount = prev.length
          const sectionProgress = Math.floor((jobStatus.progress / 100) * sectionCount)
          return prev.map((s, i) => {
            if (i < sectionProgress) return { ...s, status: 'completed' as const, progress: 100 }
            if (i === sectionProgress && jobStatus.status === 'running') {
              return { ...s, status: 'in_progress' as const, progress: 50 }
            }
            return s
          })
        })

        // Check if completed or failed
        if (jobStatus.status === 'completed') {
          clearInterval(pollingRef.current)
          pollingRef.current = undefined
          clearActiveJob()

          // Mark all sections as completed
          setSections(prev => prev.map(s => ({ ...s, status: 'completed' as const, progress: 100 })))
          setOverallProgress(100)
          setStatus('completed')

          // Fetch the report content
          let reportContent = ''
          if (jobStatus.report_id) {
            const content = await downloadReport(jobStatus.report_id)
            reportContent = content || ''
          }

          // Create report object with quality if available
          const newReport: ExtendedReport = {
            id: jobStatus.report_id,
            ticker: jobStatus.ticker,
            companyName: jobStatus.ticker,
            reportType: currentReportType,
            createdAt: jobStatus.completed_at || new Date().toISOString(),
            status: 'completed',
            filePath: `/api/reports/${jobStatus.report_id}/download`,
            content: reportContent,
            report_id: jobStatus.report_id,
            download_url: `/api/reports/${jobStatus.report_id}/download`,
            quality: jobStatus.quality || undefined,
          }
          setGeneratedReport(newReport)

        } else if (jobStatus.status === 'failed') {
          clearInterval(pollingRef.current)
          pollingRef.current = undefined
          clearActiveJob()
          setStatus('failed')
          setErrorMessage(jobStatus.error || jobStatus.message || 'Report generation failed')
          setSections(prev => prev.map(s =>
            s.status === 'in_progress' ? { ...s, status: 'failed' as const } : s
          ))
        }
      } catch (pollError) {
        console.error('Polling error:', pollError)
      }
    }, 2000)
  }, [])

  // Reset sections when company type changes
  useEffect(() => {
    const newSections = getSectionsForCompanyType(company)
    setSections(newSections.map(s => ({ ...s, status: 'pending', progress: 0 })))
    setSelectedSections(new Set(newSections.map(s => s.id)))
    setStatus('idle')
    setOverallProgress(0)
    setGeneratedReport(null)
    setErrorMessage(null)
  }, [company?.ticker, company?.quoteType, isPrivate, isETF, isMutualFund])

  // Load recent reports on mount
  useEffect(() => {
    async function loadReports() {
      const result = await listReports()
      if (result.data?.reports) {
        setRecentReports(result.data.reports)
      }
    }
    loadReports()
  }, [generatedReport]) // Reload when a new report is generated

  // Check for and resume active job on mount
  useEffect(() => {
    mountedRef.current = true

    const activeJob = loadActiveJob()
    if (activeJob && company?.ticker === activeJob.ticker) {
      // Resume polling for active job
      setStatus('generating')
      setOverallProgress(activeJob.progress)
      setReportType(activeJob.reportType as ReportType)
      setStatusMessage('Resuming report generation...')

      // Update sections based on saved progress
      // Use actual sections length for correct progress mapping across company types
      setSections(prev => {
        const sectionCount = prev.length
        const sectionProgress = Math.floor((activeJob.progress / 100) * sectionCount)
        return prev.map((s, i) => {
          if (i < sectionProgress) return { ...s, status: 'completed' as const, progress: 100 }
          if (i === sectionProgress) return { ...s, status: 'in_progress' as const, progress: 50 }
          return { ...s, status: 'pending' as const, progress: 0 }
        })
      })

      // Start polling
      startPolling(activeJob.jobId, activeJob.reportType as ReportType)
    }

    return () => {
      mountedRef.current = false
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [company?.ticker, startPolling])

  if (!company) {
    return (
      <div className="flex items-center justify-center h-full text-term-textDim">
        <p>Select a company to generate a due diligence report</p>
      </div>
    )
  }

  // Handle section toggle for custom report
  const toggleSection = (sectionId: string) => {
    if (reportType !== 'custom') return
    setSelectedSections(prev => {
      const next = new Set(prev)
      if (next.has(sectionId)) {
        next.delete(sectionId)
      } else {
        next.add(sectionId)
      }
      return next
    })
  }

  const handleGenerate = async () => {
    setStatus('generating')
    setGeneratedReport(null)
    setErrorMessage(null)
    setOverallProgress(0)

    // Get the correct sections for this company type
    const currentSections = getSectionsForCompanyType(company)

    // Reset section states and immediately set first section to in_progress
    // This provides instant visual feedback (spinning indicator) before the first poll
    setSections(currentSections.map((s, i) => ({
      ...s,
      status: i === 0 ? 'in_progress' as const : 'pending' as const,
      progress: i === 0 ? 10 : 0
    })))

    // Clear any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = undefined
    }

    try {
      // Start report generation - returns job ID immediately
      // Pass reportType so backend can do 1 loop (quick) or 3 loops (full)
      const result = await generateReport(company.ticker, { reportType, isPrivate })

      if (result.error) {
        setStatus('failed')
        setErrorMessage(result.error)
        return
      }

      // Backend returns job_id directly (no success field wrapper)
      if (result.data?.job_id) {
        const jobId = result.data.job_id
        setOverallProgress(5)

        // Save active job to localStorage
        saveActiveJob({
          jobId,
          ticker: company.ticker,
          reportType,
          startedAt: Date.now(),
          progress: 5,
        })

        // Start polling for status updates
        startPolling(jobId, reportType)
      } else {
        setStatus('failed')
        setErrorMessage(result.error || 'Failed to start report generation - no job_id returned')
      }
    } catch (error) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = undefined
      }
      clearActiveJob()
      setStatus('failed')
      setErrorMessage(error instanceof Error ? error.message : 'Unknown error occurred')
    }
  }

  const getActiveSections = () => {
    if (reportType === 'quick') return sections.slice(0, 5)
    if (reportType === 'custom') return sections.filter(s => selectedSections.has(s.id))
    return sections
  }

  // Download report as markdown
  const handleDownload = useCallback((report: ExtendedReport) => {
    const content = report.content || generatePlaceholderContent(report)
    const blob = new Blob([content], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${report.ticker}_DD_Report_${new Date(report.createdAt).toISOString().split('T')[0]}.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [])

  // View report
  const handleViewReport = useCallback((report: ExtendedReport) => {
    setViewingReport(report)
    if (onViewReport) {
      onViewReport(report)
    }
  }, [onViewReport])

  // Close report viewer
  const handleCloseViewer = useCallback(() => {
    setViewingReport(null)
  }, [])

  // If viewing a report, show the report viewer
  if (viewingReport) {
    return (
      <ReportViewer
        report={viewingReport}
        onClose={handleCloseViewer}
        onDownload={handleDownload}
      />
    )
  }

  return (
    <div className="space-y-6">
      {/* Company Type Banners */}
      {isPrivate && (
        <div className="bg-term-info/20 border border-term-info rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h3 className="text-term-info font-bold text-lg">
                Private Company DD Report
              </h3>
              <p className="text-term-textDim text-sm">
                Since {company.name} is a private company, this report will focus on publicly available
                information, industry research, and qualitative analysis. A comprehensive disclaimer
                will be included noting the limitations of available data.
              </p>
              <p className="text-term-info text-sm mt-1">
                💡 For financial analysis, request documents via the Data Room tab.
              </p>
            </div>
          </div>
        </div>
      )}

      {isETF && (
        <div className="bg-term-warning/20 border border-term-warning rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📊</span>
            <div>
              <h3 className="text-term-warning font-bold text-lg">
                ETF Analysis Report
              </h3>
              <p className="text-term-textDim text-sm">
                {company.name} is an Exchange-Traded Fund. This report focuses on holdings analysis,
                expense ratios, tracking error, sector allocation, and performance vs benchmark.
              </p>
              <p className="text-term-warning text-sm mt-1">
                ⚠️ ETF analysis differs significantly from single-company due diligence.
              </p>
            </div>
          </div>
        </div>
      )}

      {isMutualFund && (
        <div className="bg-term-warning/20 border border-term-warning rounded-lg p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📈</span>
            <div>
              <h3 className="text-term-warning font-bold text-lg">
                Mutual Fund Analysis Report
              </h3>
              <p className="text-term-textDim text-sm">
                {company.name} is a Mutual Fund. This report focuses on manager track record,
                investment style, fee structure, portfolio turnover, and performance history.
              </p>
              <p className="text-term-warning text-sm mt-1">
                ⚠️ Mutual fund analysis differs significantly from single-company due diligence.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-term-text">
          📄 DD Report - {company.ticker}
          {companyTypeLabel !== 'EQUITY' && (
            <span className={`ml-2 px-2 py-0.5 text-xs rounded font-bold ${
              isPrivate ? 'bg-term-info/30 text-term-info' :
              (isETF || isMutualFund) ? 'bg-term-warning/30 text-term-warning' :
              'bg-term-primary/30 text-term-primary'
            }`}>
              {companyTypeLabel}
            </span>
          )}
        </h2>
        {status === 'completed' && generatedReport && (
          <div className="flex gap-2">
            <button
              onClick={() => handleViewReport(generatedReport)}
              className="term-button term-button-primary"
            >
              View Report
            </button>
            <button
              onClick={() => handleDownload(generatedReport)}
              className="term-button term-button-success"
            >
              Download Report
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="term-box p-4 border-term-error bg-term-error/10">
          <div className="flex items-center gap-2 text-term-error">
            <span>Error:</span>
            <span>{errorMessage}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Report Configuration */}
        <div className="term-box p-4">
          <h3 className="text-sm font-medium text-term-text mb-3">Report Type</h3>
          <div className="space-y-2">
            {(['quick', 'full', 'custom'] as const).map((type) => (
              <button
                key={type}
                onClick={() => setReportType(type)}
                disabled={status === 'generating'}
                className={`w-full text-left p-3 rounded border transition-colors ${
                  reportType === type
                    ? 'border-term-primary bg-term-primary/10'
                    : 'border-term-border hover:border-term-primary/50'
                } ${status === 'generating' ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="font-medium text-term-text capitalize">{type}</div>
                <div className="text-xs text-term-textDim">
                  {type === 'quick' && 'Quick analysis, ~1-2 min'}
                  {type === 'full' && 'Full DD report, ~3-5 min'}
                  {type === 'custom' && 'Select sections below'}
                </div>
              </button>
            ))}
          </div>

          {/* Custom section selection */}
          {reportType === 'custom' && (
            <div className="mt-4 p-3 bg-term-bgDark rounded border border-term-border">
              <h4 className="text-xs font-medium text-term-textDim mb-2">Select Sections</h4>
              <div className="space-y-1">
                {sections.map((section) => (
                  <label
                    key={section.id}
                    className="flex items-center gap-2 py-1 cursor-pointer hover:bg-term-bgLight/30 px-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSections.has(section.id)}
                      onChange={() => toggleSection(section.id)}
                      disabled={status === 'generating'}
                      className="term-checkbox"
                    />
                    <span className={`text-sm ${selectedSections.has(section.id) ? 'text-term-text' : 'text-term-textDim'}`}>
                      {section.name}
                    </span>
                  </label>
                ))}
              </div>
              <div className="mt-2 text-xs text-term-textDim">
                {selectedSections.size} section{selectedSections.size !== 1 ? 's' : ''} selected
              </div>
            </div>
          )}

          <button
            onClick={handleGenerate}
            disabled={status === 'generating' || (reportType === 'custom' && selectedSections.size === 0)}
            className="term-button term-button-primary w-full mt-4 disabled:opacity-50"
          >
            {status === 'generating' ? 'Generating...' : 'Generate Report'}
          </button>

          {/* Data Sources Info */}
          <div className="mt-4 p-3 bg-term-bgDark rounded border border-term-border">
            <h4 className="text-xs font-medium text-term-textDim mb-2">
              Data Sources ({companyTypeLabel})
            </h4>
            {isPrivate ? (
              <div className="space-y-1 text-xs text-term-textDim">
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Dual LLM Analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Web Research & News</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Industry Reports</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Google Dorking</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>LinkedIn & Glassdoor</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-warning">○</span>
                  <span className="text-term-warning">No SEC Filings</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-warning">○</span>
                  <span className="text-term-warning">No Financial APIs</span>
                </div>
              </div>
            ) : isETF ? (
              <div className="space-y-1 text-xs text-term-textDim">
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>ETF Holdings Data</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Expense Ratios & Fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Benchmark Comparisons</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Sector Allocations</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Performance History</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-warning">○</span>
                  <span className="text-term-warning">No Company Financials</span>
                </div>
              </div>
            ) : isMutualFund ? (
              <div className="space-y-1 text-xs text-term-textDim">
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Fund Holdings Data</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Manager Track Record</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Fee Structure & Loads</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Investment Style Analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Performance History</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-warning">○</span>
                  <span className="text-term-warning">No Company Financials</span>
                </div>
              </div>
            ) : (
              <div className="space-y-1 text-xs text-term-textDim">
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Dual LLM Analysis</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Web Research (Firecrawl)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>SEC EDGAR Filings</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-term-success">✓</span>
                  <span>Financial APIs</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="col-span-2 space-y-4">
          {/* Overall Progress */}
          {status !== 'idle' && (
            <div className="term-box p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-term-text">Generation Progress</h3>
                <span className={`font-medium ${status === 'failed' ? 'text-term-error' : 'text-term-primary'}`}>
                  {status === 'failed' ? 'Failed' : `${overallProgress}%`}
                </span>
              </div>
              <div className="term-progress h-3">
                <div
                  className={`term-progress-bar ${status === 'failed' ? 'bg-term-error' : 'bg-term-primary'}`}
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
              {status === 'generating' && (
                <p className="mt-2 text-xs text-term-textDim">
                  {statusMessage || 'Generating real DD report using AI-powered analysis. This may take 3-5 minutes...'}
                </p>
              )}
            </div>
          )}

          {/* Report Quality Assessment */}
          {status === 'completed' && generatedReport?.quality && (
            <ReportQualityPanel quality={generatedReport.quality} />
          )}

          {/* Section Progress */}
          <div className="term-box p-4">
            <h3 className="text-sm font-medium text-term-text mb-3">Report Sections</h3>
            <div className="space-y-2">
              {getActiveSections().map((section) => (
                <SectionRow
                  key={section.id}
                  section={section}
                  isCustom={reportType === 'custom'}
                  isSelected={selectedSections.has(section.id)}
                  onToggle={() => toggleSection(section.id)}
                  disabled={status === 'generating'}
                />
              ))}
            </div>
          </div>

          {/* Recent Reports */}
          <div className="term-box p-4">
            <h3 className="text-sm font-medium text-term-text mb-3">Recent Reports</h3>
            {recentReports.length > 0 ? (
              <table className="term-table">
                <thead>
                  <tr>
                    <th>Ticker</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentReports.slice(0, 5).map((report) => (
                    <tr key={report.report_id}>
                      <td className="font-medium">{report.ticker}</td>
                      <td className="text-term-textDim">
                        {report.created_at
                          ? new Date(report.created_at).toLocaleDateString()
                          : report.modified
                            ? new Date(report.modified * 1000).toLocaleDateString()
                            : 'N/A'}
                      </td>
                      <td className="text-term-textDim">
                        {report.type || 'quick'}
                      </td>
                      <td>
                        <a
                          href={`/api/reports/${report.report_id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-term-primary hover:underline text-sm"
                        >
                          Download
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-term-textDim text-sm">No recent reports found. Generate a report to see it here.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface SectionRowProps {
  section: ReportSection
  isCustom?: boolean
  isSelected?: boolean
  onToggle?: () => void
  disabled?: boolean
}

function SectionRow({ section }: SectionRowProps) {
  const getStatusIcon = () => {
    switch (section.status) {
      case 'completed':
        return <span className="text-term-success text-lg">●</span>
      case 'in_progress':
        return (
          <span className="inline-block w-4 h-4">
            <svg className="animate-spin text-term-success" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </span>
        )
      case 'failed':
        return <span className="text-term-error text-lg">✗</span>
      default:
        return <span className="text-term-textDim text-lg">○</span>
    }
  }

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="w-5 flex items-center justify-center">{getStatusIcon()}</div>
      <span className={`flex-1 ${section.status === 'pending' ? 'text-term-textDim' : 'text-term-text'}`}>
        {section.name}
      </span>
      {section.status === 'in_progress' && (
        <div className="w-24">
          <div className="term-progress h-1.5">
            <div
              className="term-progress-bar bg-term-success"
              style={{ width: `${section.progress}%` }}
            />
          </div>
        </div>
      )}
      {section.status === 'completed' && (
        <span className="text-xs text-term-success">✓ Done</span>
      )}
    </div>
  )
}

// Report Viewer Component
interface ReportViewerProps {
  report: ExtendedReport
  onClose: () => void
  onDownload: (report: ExtendedReport) => void
}

function ReportViewer({ report, onClose, onDownload }: ReportViewerProps) {
  const content = report.content || generatePlaceholderContent(report)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-term-text">
          📄 {report.ticker} - {report.companyName} DD Report
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => onDownload(report)}
            className="term-button term-button-success"
          >
            Download
          </button>
          <button
            onClick={onClose}
            className="term-button"
          >
            Close
          </button>
        </div>
      </div>

      {/* Report Meta */}
      <div className="flex gap-4 text-sm text-term-textDim">
        <span>Company: {report.companyName}</span>
        <span>-</span>
        <span>Generated: {new Date(report.createdAt).toLocaleString()}</span>
      </div>

      {/* Report Content */}
      <div className="term-box p-6 bg-term-bgDark max-h-[60vh] overflow-auto">
        <pre className="whitespace-pre-wrap font-mono text-sm text-term-text leading-relaxed">
          {content}
        </pre>
      </div>
    </div>
  )
}

// Fallback placeholder content (only used if API fails to return content)
function generatePlaceholderContent(report: ExtendedReport): string {
  const date = new Date(report.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return `# Due Diligence Report: ${report.ticker}
## ${report.companyName}

**Generated:** ${date}

---

Report content is being loaded...

If you see this message, the report may still be generating or there was an issue loading the content.

Please try downloading the report directly or generating a new one.

---

*This is a placeholder. Real reports include comprehensive financial analysis, regime classification, risk assessment, and data room suggestions.*
`
}

// Report Quality Panel Component - Simplified 3-metric display
interface ReportQualityPanelProps {
  quality: ReportQuality
}

function ReportQualityPanel({ quality }: ReportQualityPanelProps) {
  // Calculate confidence score from accuracy + source trust (50/50 weighted)
  const confidenceScore = Math.round((quality.averageAccuracy + quality.averageSourceTrust) / 2)

  // Freshness score is the recency metric
  const freshnessScore = quality.averageRecency

  const gradeColors: Record<ReportQuality['grade'], string> = {
    A: 'border-green-500 bg-green-900/20',
    B: 'border-blue-500 bg-blue-900/20',
    C: 'border-yellow-500 bg-yellow-900/20',
    D: 'border-orange-500 bg-orange-900/20',
    F: 'border-red-500 bg-red-900/20',
  }

  const gradeTextColors: Record<ReportQuality['grade'], string> = {
    A: 'text-green-400',
    B: 'text-blue-400',
    C: 'text-yellow-400',
    D: 'text-orange-400',
    F: 'text-red-400',
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400'
    if (score >= 60) return 'text-blue-400'
    if (score >= 40) return 'text-yellow-400'
    return 'text-red-400'
  }

  const getProgressBar = (score: number) => {
    const filled = Math.round(score / 10)
    return (
      <div className="flex gap-0.5 w-full">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className={`flex-1 h-1.5 rounded-sm ${
              i < filled
                ? score >= 70 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                : 'bg-term-border'
            }`}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="term-box p-4 border border-term-border">
      {/* Header */}
      <h3 className="text-sm font-bold text-term-text mb-4 flex items-center gap-2">
        📊 Report Quality
      </h3>

      {/* Three Key Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {/* Overall Grade - Primary */}
        <div className={`p-3 rounded-lg border-2 text-center ${gradeColors[quality.grade]}`}>
          <div className={`text-3xl font-bold ${gradeTextColors[quality.grade]}`}>
            {quality.grade}
          </div>
          <div className="text-xs text-term-textDim mt-1">Overall Grade</div>
          <div className={`text-xs ${gradeTextColors[quality.grade]} opacity-80`}>
            {quality.overallScore}/100
          </div>
        </div>

        {/* Confidence Score */}
        <div className="p-3 rounded-lg border border-term-border bg-term-bgLight/30 text-center">
          <div className={`text-2xl font-bold ${getScoreColor(confidenceScore)}`}>
            {confidenceScore}%
          </div>
          <div className="text-xs text-term-textDim mt-1">Confidence</div>
          <div className="mt-2">
            {getProgressBar(confidenceScore)}
          </div>
        </div>

        {/* Freshness Score */}
        <div className="p-3 rounded-lg border border-term-border bg-term-bgLight/30 text-center">
          <div className={`text-2xl font-bold ${getScoreColor(freshnessScore)}`}>
            {freshnessScore}%
          </div>
          <div className="text-xs text-term-textDim mt-1">Freshness</div>
          <div className="mt-2">
            {getProgressBar(freshnessScore)}
          </div>
        </div>
      </div>

      {/* Issues Summary - Compact */}
      {(quality.totalConflicts > 0 || quality.totalGaps > 0 || quality.weakSections.length > 0) && (
        <div className="flex flex-wrap gap-2 text-xs mb-3">
          {quality.totalConflicts > 0 && (
            <span className="px-2 py-0.5 rounded bg-yellow-900/30 text-yellow-400">
              ⚠️ {quality.totalConflicts} conflict{quality.totalConflicts !== 1 ? 's' : ''}
            </span>
          )}
          {quality.totalGaps > 0 && (
            <span className="px-2 py-0.5 rounded bg-orange-900/30 text-orange-400">
              📋 {quality.totalGaps} gap{quality.totalGaps !== 1 ? 's' : ''}
            </span>
          )}
          {quality.weakSections.length > 0 && (
            <span className="px-2 py-0.5 rounded bg-red-900/30 text-red-400">
              🔴 {quality.weakSections.length} weak
            </span>
          )}
        </div>
      )}

      {/* Top Suggestion - Only show most critical */}
      {quality.suggestions.length > 0 && quality.grade !== 'A' && (
        <div className="border-t border-term-border pt-3">
          <div className="flex items-start gap-2 text-xs">
            <span className="text-yellow-400">💡</span>
            <span className="text-term-textDim">{quality.suggestions[0].suggestion}</span>
          </div>
          {quality.suggestions.length > 1 && (
            <p className="text-xs text-term-textDim/60 mt-1 ml-5">
              +{quality.suggestions.length - 1} more suggestions
            </p>
          )}
        </div>
      )}
    </div>
  )
}
